import React, {
  useState,
  useMemo,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Switch,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Alert,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
} from "@mui/material";
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  Image as ImageIcon,
  Close as CloseIcon,
  Comment as CommentIcon,
  UploadFile as UploadFileIcon,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import QuestionForm from "@/components/common/QuestionForm";
import SectionInfoEditor from "@/components/admin/SectionInfoEditor";
import DialogueQuestionGroupForm from "@/components/admin/DialogueQuestionGroupForm";
import PdfUploadDialog from "@/components/admin/PdfUploadDialog";
import PdfReviewDialog from "@/components/admin/PdfReviewDialog";
import {
  openImagePreview,
  openRichTextPreview,
} from "@/store/slices/contentPreviewSlice";
import {
  useGetExerciseQuestionsQuery,
  useCreateExerciseQuestionMutation,
  useUpdateExerciseQuestionMutation,
  useDeleteExerciseQuestionMutation,
  useGetSectionInfoQuery,
  useCreateSectionInfoMutation,
  useUpdateSectionInfoMutation,
  useCreateDialogueQuestionGroupMutation,
  useUpdateDialogueQuestionGroupMutation,
  useDeleteDialogueQuestionGroupMutation,
  useLazyGetDialogueQuestionGroupQuery,
  useBulkAddExerciseDayQuestionsMutation,
} from "@/store/api/admin/exerciseApi";
import { useParsePdfMutation } from "@/store/api/adminApi";
import { PreviewAudioButton, PreviewImageButton } from "@/components/common";
import { uploadMediaFiles } from "@/utils/cloudinaryUpload";

const TOEIC_PARTS = [
  { number: 1, title: "Photographs", type: "LC" },
  { number: 2, title: "Question-Response", type: "LC" },
  { number: 3, title: "Conversations", type: "LC" },
  { number: 4, title: "Short Talks", type: "LC" },
  { number: 5, title: "Incomplete Sentences", type: "RC" },
  { number: 6, title: "Text Completion", type: "RC" },
  { number: 7, title: "Reading Comprehension", type: "RC" },
];

const QUESTION_TYPES = {
  "multiple-choice": "Multiple Choice",
  "fill-blank": "Fill in the Blank",
  "mix-match": "Mix-Match (Fill-blank content + MC answer)",
  "dialogue-group": "Dialogue Group (Fill-blanks + MC Questions)",
  short_answer: "Short Answer",
};

const ExerciseQuestionManagement = ({
  exerciseId,
  dayId,
  partType, // 'lcExercise' or 'rcExercise'
  open,
  onClose,
}) => {
  const [selectedParts, setSelectedParts] = useState([]);
  const [expandedPart, setExpandedPart] = useState(null);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [isSubmittingQuestion, setIsSubmittingQuestion] = useState(false);
  const [dialogKey, setDialogKey] = useState(0);
  const [dialogueGroupDialogOpen, setDialogueGroupDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingDialogueGroup, setEditingDialogueGroup] = useState(null);
  const [editingPart, setEditingPart] = useState(null);
  const [previousQuestion, setPreviousQuestion] = useState(null);
  const questionFormRef = useRef(null);
  const formDataRef = useRef({});
  const dispatch = useDispatch();

  // PDF upload (rcExercise only)
  const [pdfUploadOpen, setPdfUploadOpen] = useState(false);
  const [pdfParseResult, setPdfParseResult] = useState(null);
  const [pdfResultsOpen, setPdfResultsOpen] = useState(false);
  const uploadExercisePdfMutation = useParsePdfMutation();
  const bulkAddExerciseDayQuestionsTuple = useBulkAddExerciseDayQuestionsMutation();
  const isRcExercise = partType === "rcExercise";

  // API hooks with optimized configuration
  const {
    data: questionsResponse,
    isLoading,
    error,
  } = useGetExerciseQuestionsQuery(
    { dayId, partType },
    {
      skip: !dayId || !partType,
      // Prevent unnecessary refetches
      refetchOnMountOrArgChange: false,
      refetchOnFocus: false,
      refetchOnReconnect: false,
    }
  );

  const [createQuestion, { isLoading: isCreating }] =
    useCreateExerciseQuestionMutation();
  const [updateQuestion, { isLoading: isUpdating }] =
    useUpdateExerciseQuestionMutation();
  const [deleteQuestion, { isLoading: isDeleting }] =
    useDeleteExerciseQuestionMutation();
  
  // Dialogue Question Group API hooks
  const [createDialogueGroup, { isLoading: isCreatingDialogue }] =
    useCreateDialogueQuestionGroupMutation();
  const [updateDialogueGroup, { isLoading: isUpdatingDialogue }] =
    useUpdateDialogueQuestionGroupMutation();
  const [deleteDialogueGroup, { isLoading: isDeletingDialogue }] =
    useDeleteDialogueQuestionGroupMutation();
  const [getDialogueGroup] = useLazyGetDialogueQuestionGroupQuery();

  // Section Info API
  const sectionType = partType.replace(/([A-Z])/g, "-$1").toLowerCase();
  const { data: sectionInfoResponse } = useGetSectionInfoQuery(
    { dayId, sectionType },
    { skip: !dayId || !partType }
  );
  const [createSectionInfo] = useCreateSectionInfoMutation();
  const [updateSectionInfo] = useUpdateSectionInfoMutation();

  // Memoize questions to prevent unnecessary re-renders.
  // BE returns a flat array; the UI works with a partNumber-keyed map so
  // "active parts" can be derived from `Object.keys(questions)`.
  const questions = useMemo(() => {

    console.log('questionsResponse?.data', questionsResponse?.data);
    const raw = questionsResponse?.data;
    if (!raw) return {};
    if (Array.isArray(raw)) {
      const grouped = {};
      for (const q of raw) {
        const pn = Number(q?.partNumber);
        if (!Number.isInteger(pn) || pn < 1 || pn > 7) continue;
        if (!grouped[pn]) grouped[pn] = [];
        grouped[pn].push(q);
      }
      return grouped;
    }
    return raw;
  }, [questionsResponse?.data]);
  const sectionInfo = sectionInfoResponse?.data || {};
  const isAnyLoading = isLoading || isCreating || isUpdating || isDeleting;

  const totalQuestions = useMemo(() => {
    if (!questionsResponse?.data) return 0;
    const allQuestions = Object.values(questionsResponse?.data).flat();
    return allQuestions.length;
  }, [questionsResponse?.data]);

  const [questionFormData, setQuestionFormData] = useState({
    question_number: 1,
    title: "",
    question: "",
    content: "",
    type: "multiple-choice",
    options: ["", "", "", ""],
    correctAnswer: "",
    blanks: [],
    explanation: "",
    image: null,
    audio: null,
    extraImage: null,
  });

  // Filter parts based on exercise type
  const availableParts = useMemo(() => {
    const exerciseType = partType === "lcExercise" ? "LC" : "RC";
    console.log(
      "Available parts:",
      TOEIC_PARTS.filter((part) => part.type === exerciseType)
    );
    return TOEIC_PARTS.filter((part) => part.type === exerciseType);
  }, [partType]);

  // Memoize getPartQuestions to prevent unnecessary recalculations
  const getPartQuestions = useMemo(() => {
    return (partNumber) => questions?.[partNumber] || [];
  }, [questions]);

  // Debug log only when questionsResponse actually changes (development only)
  useEffect(() => {
    if (process.env.NODE_ENV === "development" && questionsResponse) {
      console.log("Questions loaded:", questionsResponse);
    }
  }, [questionsResponse]);

  // Auto-select parts that have questions
  useEffect(() => {
    if (!!questions) {
      // Get unique part numbers from questions
      const partsWithQuestions = Object.keys(questions).map(
        (partNumber) => +partNumber
      );

      // Only update if different from current selection
      const currentSelection = [...selectedParts].sort().join(",");
      const newSelection = partsWithQuestions.sort().join(",");

      if (currentSelection !== newSelection) {
        setSelectedParts(partsWithQuestions);
        // Auto-expand the first part with questions
        if (partsWithQuestions.length > 0 && !expandedPart) {
          setExpandedPart(partsWithQuestions[0]);
        }
      }
    }
  }, [questions]);

  const handlePartToggle = (partNumber) => {
    setSelectedParts((prev) => {
      if (prev.includes(partNumber)) {
        return prev.filter((p) => p !== partNumber);
      } else {
        return [...prev, partNumber];
      }
    });
  };

  const handleExpandPart = (partNumber) => {
    setExpandedPart(expandedPart === partNumber ? null : partNumber);
  };

  const handleOpenQuestionDialog = (partNumber, question = null) => {
    setEditingPart(partNumber);
    setEditingQuestion(question);

    const partQuestions = getPartQuestions(partNumber);

    if (question) {
      // Find previous question for editing mode
      const currentIndex = partQuestions.findIndex(
        (q) => q._id === question._id
      );
      setPreviousQuestion(
        currentIndex > 0 ? partQuestions[currentIndex - 1] : null
      );

      // Debug: Log question structure to help identify the correct field names
      console.log("Editing question structure:", question);

      // Convert backend format to QuestionForm format (simple string array)
      // Try multiple possible field names for answers/options/choices
      const answersList =
        question.answers || question.options || question.choices || [];
      console.log("Answers list:", answersList);
      const options =
        answersList.length > 0
          ? answersList.map((a) => (a.text !== undefined ? a.text : a))
          : ["", "", "", ""];

      console.log(
        "Parsed options:",
        options,
        answersList.map((a) => a.text || a)
      );

      // Find correct answer - try multiple approaches
      let correctAnswer = "";
      if (question.correctAnswer) {
        // If correctAnswer is already set (like "A", "B", etc.)
        correctAnswer = question.correctAnswer;
      } else {
        // Find from answers array
        const correctAnswerIndex = answersList.findIndex((a) => a.isCorrect);
        correctAnswer =
          correctAnswerIndex >= 0
            ? String.fromCharCode(65 + correctAnswerIndex)
            : "";
      }

      console.log("Parsed correctAnswer:", correctAnswer);

      setQuestionFormData({
        question_number: question.number || 1,
        title: question.title || "",
        question: question.question || "",
        content: question.mainContent || question.content || "",
        type: question.type || "multiple-choice",
        options,
        correctAnswer,
        blanks: question.blanks || [],
        explanation: question.explanation || "",
        image: null,
        audio: null,
        extraImage: null,
        imagePreview: question.imageUrl || "",
        audioPreview: question.audioUrl || "",
        extraImagePreview: question.extraImageUrl || "",
      });
    } else {
      // Set previous question to the last question in the part for new question
      setPreviousQuestion(
        partQuestions.length > 0
          ? partQuestions[partQuestions.length - 1]
          : null
      );

      const nextNumber =
        partQuestions.length > 0
          ? Math.max(...partQuestions.map((q) => q.number || 0)) + 1
          : 1;

      setQuestionFormData({
        question_number: nextNumber,
        title: "",
        question: "",
        content: "",
        type: "multiple-choice",
        options: ["", "", "", ""],
        correctAnswer: "",
        blanks: [],
        explanation: "",
        image: null,
        audio: null,
        extraImage: null,
      });
    }
    setDialogKey((prev) => prev + 1);
    setQuestionDialogOpen(true);
  };

  const handleCloseQuestionDialog = () => {
    setQuestionDialogOpen(false);
    setEditingQuestion(null);
    setEditingPart(null);
    setPreviousQuestion(null);
    // Reset rich text editors
    if (questionFormRef.current) {
      questionFormRef.current.resetRichText();
    }
    setQuestionFormData({
      question_number: 1,
      title: "",
      question: "",
      content: "",
      type: "multiple-choice",
      options: ["", "", "", ""],
      correctAnswer: "",
      blanks: [],
      explanation: "",
      image: null,
      audio: null,
      extraImage: null,
    });
  };

  const handleQuestionFormChange = useCallback((formData) => {
    formDataRef.current = formData;
  }, []);

  const handleSubmitQuestion = async () => {
    if (isSubmittingQuestion) return;
    setIsSubmittingQuestion(true);
    try {
      // Process images in rich text editors and get final data
      let currentFormData = formDataRef.current;
      if (questionFormRef.current) {
        currentFormData =
          await questionFormRef.current.processImagesAndGetData();
      }
      
      // if (!currentFormData.question.trim()) {
      //   toast.error("Question text is required");
      //   return;
      // }

      if (currentFormData.type === "multiple-choice" || currentFormData.type === "mix-match") {
        if (!currentFormData.correctAnswer) {
          toast.error(
            "Please select a correct answer for multiple choice questions"
          );
          return;
        }
      }

      if (currentFormData.type === "fill-blank") {
        if (!currentFormData.blanks || currentFormData.blanks.length === 0) {
          toast.error(
            "Please add at least one blank for fill-in-the-blank questions"
          );
          return;
        }
        const hasEmptyBlanks = currentFormData.blanks.some(
          (blank) =>
            !blank.correctAnswers ||
            blank.correctAnswers.length === 0 ||
            blank.correctAnswers.every((a) => !a.trim())
        );
        if (hasEmptyBlanks) {
          toast.error("Each blank must have at least one correct answer");
          return;
        }
      }

      // Upload media files directly to Cloudinary
      const mediaFiles = {
        image: currentFormData.image,
        audio: currentFormData.audio,
        extraImage: currentFormData.extraImage,
      };
      
      const uploadedUrls = await uploadMediaFiles(mediaFiles, `toeic/exercises/${dayId}`);
     
      if (currentFormData.type === "mix-match") {
        if (!currentFormData.content || !currentFormData.content.trim()) {
          toast.error("Content with blanks is required for mix-match questions");
          return;
        }
      }

      // Create FormData for file uploads
      const formData = new FormData();

      // Append basic fields
      formData.append("title", currentFormData.title || "");
      formData.append("question", currentFormData.question);
      formData.append("number", currentFormData.question_number);
      formData.append("type", currentFormData.type);

      // Sanitize and append content
      const sanitizedContent = currentFormData.content
        ? currentFormData.content.replace(/<[^>]*>/g, "").trim()
        : "";
      formData.append(
        "content",
        sanitizedContent.length > 0 ? currentFormData.content : ""
      );
      formData.append("explanation", currentFormData.explanation || "");

      // Handle answers based on question type
      if (currentFormData.type === "multiple-choice" || currentFormData.type === "mix-match") {
        const answers = currentFormData.options.map((optionText, idx) => {
          if (typeof optionText === "object") return optionText;
          return {
            text: optionText,
            value: String.fromCharCode(65 + idx),
            isCorrect:
              String.fromCharCode(65 + idx) ===
              currentFormData.correctAnswer?.toUpperCase(),
          };
        });
        formData.append("answers", JSON.stringify(answers));
      } else if (currentFormData.type === "fill-blank") {
        formData.append("blanks", JSON.stringify(currentFormData.blanks));
      } else if (currentFormData.type === "short_answer") {
        formData.append("correctAnswer", currentFormData.correctAnswer);
      }

      // Append uploaded URLs or existing URLs
      ["image", "audio", "extraImage"].forEach((mediaName) => {
        const url = `${mediaName}Url`;
        if (uploadedUrls[url]) {
          formData.append(url, uploadedUrls[url]);
        } else if (Object.prototype.hasOwnProperty.call(currentFormData, url)) {
          formData.append(url, currentFormData[url] ?? "");
        }
      });

      if (editingQuestion) {
        await updateQuestion({
          questionId: editingQuestion._id,
          questionData: formData,
        }).unwrap();
        toast.success("Question updated successfully");
      } else {
        await createQuestion({
          dayId,
          partType,
          partNumber: editingPart,
          questionData: formData,
        }).unwrap();
        toast.success("Question created successfully");
      }
      handleCloseQuestionDialog();
    } catch (error) {
      console.error("Failed to save question:", error);
      toast.error(error?.data?.message || "Failed to save question");
    } finally {
      setIsSubmittingQuestion(false);
    }
  };

  const handleSaveSectionInfo = async (sectionData) => {
    try {
      // Check if section info exists (has an _id or id)
      const sectionExists = sectionInfo?._id || sectionInfo?.id;

      if (sectionExists) {
        // Update existing section info
        await updateSectionInfo({
          dayId,
          sectionType,
          sectionData,
        }).unwrap();
      } else {
        // Create new section info
        await createSectionInfo({
          dayId,
          sectionType,
          sectionData,
        }).unwrap();
      }
      // Success toast is handled in SectionInfoEditor
    } catch (error) {
      throw error; // Re-throw to be handled by SectionInfoEditor
    }
  };

  const handleDeleteQuestion = async (partNumber, questionId) => {
    if (!window.confirm("Are you sure you want to delete this question?")) {
      return;
    }

    try {
      await deleteQuestion(questionId).unwrap();
      toast.success("Question deleted successfully");
    } catch (error) {
      console.error("Failed to delete question:", error);
      toast.error(error?.data?.message || "Failed to delete question");
    }
  };

  // Dialogue Group Handlers
  const handleOpenDialogueGroupDialog = (partNumber) => {
    setEditingPart(partNumber);
    
    // Calculate next question number for new dialogue group
    const partQuestions = getPartQuestions(partNumber);
    const nextNumber =
      partQuestions.length > 0
        ? Math.max(...partQuestions.map((q) => q.number || 0)) + 1
        : 1;
    
    // Set initial data with auto-filled question number
    setEditingDialogueGroup({ nextQuestionNumber: nextNumber });
    setDialogueGroupDialogOpen(true);
  };

  const handleEditDialogueGroup = async (parentQuestion) => {
    try {
      // Fetch full dialogue group data with child questions
      const fullData = await getDialogueGroup(parentQuestion._id).unwrap();
      console.log('Fetched dialogue group data:', fullData);
      
      // Store the full data with the parent question ID
      const dialogueGroupData = {
        ...fullData.data,
        parentQuestionId: parentQuestion._id, // Store the ID for updates
      };
      
      setEditingDialogueGroup(dialogueGroupData);
      setEditingPart(parentQuestion.partNumber || 3);
      setDialogueGroupDialogOpen(true);
    } catch (error) {
      console.error("Failed to fetch dialogue group:", error);
      toast.error("Failed to load dialogue group data");
    }
  };

  const handleCloseDialogueGroupDialog = () => {
    setDialogueGroupDialogOpen(false);
    setEditingDialogueGroup(null);
  };

  const handleSubmitDialogueGroup = async (dialogueData) => {
    try {
      console.log("dialogueData", dialogueData);
      console.log("dialogueData instanceof FormData:", dialogueData instanceof FormData);
      console.log("editingDialogueGroup", editingDialogueGroup);
      
      // Check if we have a parentQuestionId (editing existing) or not (creating new)
      const parentQuestionId = editingDialogueGroup?.parentQuestionId || 
                               editingDialogueGroup?.parentQuestion?._id ||
                               editingDialogueGroup?._id;
      
      if (parentQuestionId) {
        // Update existing dialogue group
        console.log("Updating with parentQuestionId:", parentQuestionId);
        
        await updateDialogueGroup({
          parentQuestionId,
          dialogueData, // FormData or regular object
        }).unwrap();
        
        toast.success("Dialogue group updated successfully");
      } else {
        // Create new dialogue group
        console.log("Creating new dialogue group");
        await createDialogueGroup(dialogueData).unwrap();
        toast.success("Dialogue group created successfully");
      }
      handleCloseDialogueGroupDialog();
    } catch (error) {
      console.error("Failed to save dialogue group:", error);
      toast.error(error?.data?.message || "Failed to save dialogue group");
    }
  };

  const handleDeleteDialogueGroup = async (parentQuestionId) => {
    if (!window.confirm("Are you sure you want to delete this dialogue group? This will delete the parent question and all child questions.")) {
      return;
    }

    try {
      await deleteDialogueGroup(parentQuestionId).unwrap();
      toast.success("Dialogue group deleted successfully");
    } catch (error) {
      console.error("Failed to delete dialogue group:", error);
      toast.error(error?.data?.message || "Failed to delete dialogue group");
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xl" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h5">
            {partType === "lcExercise" ? "LC Exercise" : "RC Exercise"} -
            Question Management
          </Typography>
          <Box sx={{ display: "flex", gap: 1, alignItems: "center" }}>
            {isRcExercise && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadFileIcon />}
                onClick={() => setPdfUploadOpen(true)}
              >
                Upload PDF (RC)
              </Button>
            )}
            <IconButton onClick={onClose}>
              <CloseIcon />
            </IconButton>
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box>
          {/* Section Info Editor */}
          <SectionInfoEditor
            sectionType={sectionType}
            dayId={dayId}
            initialData={sectionInfo}
            onSave={handleSaveSectionInfo}
          />
          {/* Loading State */}
          {isLoading && (
            <Box display="flex" justifyContent="center" my={3}>
              <CircularProgress />
            </Box>
          )}

          {/* Error State */}
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              Failed to load questions: {error?.data?.message || error.message}
            </Alert>
          )}

          {/* Questions Header */}
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={2}
          >
            <Typography variant="h6">Questions by Part</Typography>
            <Chip
              label={`${totalQuestions} Total Questions`}
              color="primary"
            />
          </Box>

          {/* Part Selection */}
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Select TOEIC Parts to Include
              </Typography>
              <Grid container spacing={2}>
                {availableParts.map((part) => (
                  <Grid item xs={12} sm={6} md={4} key={part.number}>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={selectedParts.includes(+part.number)}
                          onChange={() => handlePartToggle(+part.number)}
                          color="primary"
                          disabled={isAnyLoading}
                        />
                      }
                      label={`Part ${part.number}: ${part.title}`}
                    />
                  </Grid>
                ))}
              </Grid>
            </CardContent>
          </Card>

          {/* Parts and Questions */}
          {selectedParts
            .sort((a, b) => a - b)
            .map((partNumber) => {
              const part = availableParts.find(
                (p) => +p.number === +partNumber
              );
              const partQuestions = getPartQuestions(partNumber);

              return (
                <Accordion
                  key={partNumber}
                  expanded={expandedPart === partNumber}
                  onChange={() => handleExpandPart(partNumber)}
                  sx={{ mb: 2 }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      width="100%"
                    >
                      <Typography variant="h6">
                        Part {part?.number}: {part?.title}
                      </Typography>
                      <Chip
                        label={`${partQuestions.length} Questions`}
                        color="primary"
                        size="small"
                      />
                    </Box>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={2}
                      >
                        <Box display="flex" gap={1}>
                          <Button
                            variant="contained"
                            startIcon={<AddIcon />}
                            onClick={() => handleOpenQuestionDialog(partNumber)}
                            disabled={isAnyLoading}
                          >
                            Add Question
                          </Button>
                          {/* Dialogue Group button only for LC Parts 3 & 4 */}
                          {partType === "lcExercise" && (partNumber === 3 || partNumber === 4) && (
                            <Button
                              variant="contained"
                              color="secondary"
                              startIcon={<CommentIcon />}
                              onClick={() => handleOpenDialogueGroupDialog(partNumber)}
                              disabled={isAnyLoading}
                            >
                              Add Dialogue Group
                            </Button>
                          )}
                        </Box>
                      </Box>

                      {/* Questions List */}
                      <List>
                        {partQuestions.map((question, index) => (
                          <ListItem key={question._id} divider>
                            <ListItemText
                              primary={
                                <Box>
                                  <Typography
                                    variant="body1"
                                    fontWeight="medium"
                                    // Maximum 1 line
                                    noWrap
                                  >
                                    {question.number || index + 1}.{" "}
                                    {question.question || question.title || question.dialogueScript}
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    sx={{ ml: 1 }}
                                  >
                                    (
                                    {QUESTION_TYPES[question.type] || "Unknown"}
                                    )
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box>
                                  <Typography
                                    variant="body2"
                                    color="text.secondary"
                                  >
                                    {(question.type === "multiple-choice" || question.type === "mix-match") &&
                                      question.correctAnswer && (
                                        <>
                                          Correct Answer:{" "}
                                          {question.correctAnswer}
                                          {question.choices?.find(
                                            (a) => a.isCorrect
                                          ) && (
                                            <>
                                              .{" "}
                                              {
                                                question.choices.find(
                                                  (a) => a.isCorrect
                                                ).text
                                              }
                                            </>
                                          )}
                                        </>
                                      )}
                                    {question.type === "fill-blank" && (
                                      <>
                                        Blanks: {question.blanks?.length || 0}
                                      </>
                                    )}
                                    {question.type === "mix-match" && question.content && (
                                      <>
                                        Has content with blanks
                                      </>
                                    )}
                                    {question.type === "dialogue-group" && (
                                      <>
                                        <Chip 
                                          label={`${question.blanks?.length || 0} Fill-in-blanks`} 
                                          size="small" 
                                          color="primary" 
                                          variant="outlined"
                                          sx={{ mr: 1 }}
                                        />
                                        <Chip 
                                          label={`${question.childQuestions?.length || 0} Comprehension Qs`} 
                                          size="small" 
                                          color="secondary" 
                                          variant="outlined"
                                        />
                                      </>
                                    )}
                                  </Typography>
                                  <Box display="flex" gap={1} mt={1}>
                                    {question.audioUrl && (
                                      <PreviewAudioButton
                                        audioUrl={question.audioUrl}
                                      />
                                    )}
                                    {question.imageUrl && (
                                      <PreviewImageButton
                                        imageUrl={question.imageUrl}
                                        label="Question Image"
                                      />
                                    )}
                                    {question.extraImageUrl && (
                                      <PreviewImageButton
                                        imageUrl={question.extraImageUrl}
                                        label="Extra Image"
                                        color="secondary"
                                      />
                                    )}
                                    {(question.content || question.mainContent) &&
                                      (question.content || question.mainContent) !== "<br>" && (
                                        <IconButton
                                          size="small"
                                          aria-label="content"
                                          onClick={() =>
                                            dispatch(
                                              openRichTextPreview(
                                                question.content ||
                                                  question.mainContent
                                              )
                                            )
                                          }
                                        >
                                          <CommentIcon />
                                        </IconButton>
                                      )}
                                  </Box>
                                </Box>
                              }
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                color="primary"
                                onClick={() => {
                                  if (question.type === 'dialogue-group') {
                                    handleEditDialogueGroup(question);
                                  } else {
                                    handleOpenQuestionDialog(partNumber, question);
                                  }
                                }}
                                disabled={isAnyLoading}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon />
                              </IconButton>
                              <IconButton
                                edge="end"
                                color="error"
                                onClick={() =>
                                  handleDeleteQuestion(partNumber, question._id)
                                }
                                disabled={isAnyLoading}
                              >
                                <DeleteIcon />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                        ))}
                      </List>

                      {partQuestions.length === 0 && (
                        <Alert severity="info">
                          No questions added yet. Click "Add Question" to create
                          the first question for this part.
                        </Alert>
                      )}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              );
            })}

          {selectedParts.length === 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Select at least one TOEIC part to start adding questions.
            </Alert>
          )}
        </Box>
      </DialogContent>

      {/* Question Form Dialog */}
      <Dialog
        open={questionDialogOpen}
        onClose={handleCloseQuestionDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingQuestion ? "Edit Question" : "Add New Question"}
          {editingPart && (
            <Typography variant="subtitle2" color="text.secondary">
              Part {editingPart}:{" "}
              {availableParts.find((p) => p.number === editingPart)?.title}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <QuestionForm
              key={dialogKey}
              ref={questionFormRef}
              initialData={questionFormData}
              onChange={handleQuestionFormChange}
              previousQuestion={previousQuestion}
              allowMultipleTypes={true}
              showContent={true}
              isEditing={!!editingQuestion}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseQuestionDialog}
            disabled={isAnyLoading || isSubmittingQuestion}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmitQuestion}
            variant="contained"
            disabled={isAnyLoading || isSubmittingQuestion}
          >
            {(isCreating || isUpdating || isSubmittingQuestion) && (
              <CircularProgress size={20} sx={{ mr: 1 }} />
            )}
            {editingQuestion ? "Update" : "Create"} Question
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialogue Question Group Dialog */}
      <Dialog
        open={dialogueGroupDialogOpen}
        onClose={handleCloseDialogueGroupDialog}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {editingDialogueGroup ? "Edit Dialogue Question Group" : "Create Dialogue Question Group"}
          <Typography variant="subtitle2" color="text.secondary">
            Combine fill-in-the-blank dialogue with multiple-choice comprehension questions
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <DialogueQuestionGroupForm
              initialData={editingDialogueGroup}
              exerciseDay={dayId}
              section={partType === "lcExercise" ? "lc-exercise" : "rc-exercise"}
              partNumber={editingPart}
              onSubmit={handleSubmitDialogueGroup}
              onCancel={handleCloseDialogueGroupDialog}
            />
          </Box>
        </DialogContent>
      </Dialog>

      {/* PDF Upload (RC Exercise only) */}
      {isRcExercise && (
        <>
          <PdfUploadDialog
            open={pdfUploadOpen}
            onClose={() => setPdfUploadOpen(false)}
            title="Parse RC PDF — Exercise"
            description="Upload a PDF containing TOEIC Reading practice (Parts 5, 6, and 7). Optionally restrict parsing to a single PART to speed things up. Nothing is saved yet — you will review and confirm before questions are added to this day."
            uploadMutation={uploadExercisePdfMutation}
            defaultFormat="toeic-rc"
            lockFormat
            defaultOcrLang="eng"
            extraFields={[
              {
                name: "part",
                label: "PART to parse",
                type: "select",
                defaultValue: "",
                options: [
                  { value: "", label: "All RC Parts (5, 6, 7)" },
                  { value: "5", label: "PART 5 — Incomplete Sentences" },
                  { value: "6", label: "PART 6 — Text Completion" },
                  { value: "7", label: "PART 7 — Reading Comprehension" },
                ],
              },
            ]}
            onParsed={(payload) => {
              setPdfParseResult(payload);
              setPdfResultsOpen(true);
            }}
          />

          <PdfReviewDialog
            open={pdfResultsOpen}
            onClose={() => setPdfResultsOpen(false)}
            parsed={pdfParseResult}
            targetLabel={`Exercise Day ${dayId} — RC Exercise${
              pdfParseResult?.part ? ` (Part ${pdfParseResult.part})` : ""
            }`}
            sectionDefault="rc-exercise"
            defaultPartNumber={pdfParseResult?.part || undefined}
            uploadFolder={`toeic/exercises/${dayId}`}
            showContent
            submitMutation={bulkAddExerciseDayQuestionsTuple}
            buildBulkPayload={({ questions: payloadQuestions, parsed }) => {
              // If the admin restricted parsing to a single PART, send it as the
              // top-level partNumber so every question inherits it. Otherwise rely
              // on per-item `partNumber` from the parser (all parts mode).
              const topLevelPart =
                parsed?.part && Number.isInteger(Number(parsed.part))
                  ? Number(parsed.part)
                  : undefined;
              return {
                dayId,
                target: "rc-exercise",
                ...(topLevelPart ? { partNumber: topLevelPart } : {}),
                questions: payloadQuestions.map((q) => ({
                  ...q,
                  partNumber: q.partNumber ?? topLevelPart,
                })),
              };
            }}
          />
        </>
      )}
    </Dialog>
  );
};

export default ExerciseQuestionManagement;
